import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import LandingPage from './pages/LandingPage';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './context/LanguageContext';
import { ContentProvider } from './context/ContentContext';
import { initAnalytics, trackPageView } from './services/analytics';

// Lazy load non-critical pages for better performance
const UAELoanCalculator = lazy(() => import('./pages/UAELoanCalculator'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BlogPage = lazy(() => import('./pages/blog/BlogPage'));
const BlogArticle = lazy(() => import('./pages/blog/BlogArticle'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Lazy load SEO pages
const MortgageUAE = lazy(() => import('./pages/seo').then(m => ({ default: m.MortgageUAE })));
const HomeLoanDubai = lazy(() => import('./pages/seo').then(m => ({ default: m.HomeLoanDubai })));
const PersonalLoanFinderUAE = lazy(() => import('./pages/seo').then(m => ({ default: m.PersonalLoanFinderUAE })));
const MotorLoanUAE = lazy(() => import('./pages/seo').then(m => ({ default: m.MotorLoanUAE })));
const MortgageCalculatorUAE = lazy(() => import('./pages/seo').then(m => ({ default: m.MortgageCalculatorUAE })));
const RefinanceMortgageUAE = lazy(() => import('./pages/seo').then(m => ({ default: m.RefinanceMortgageUAE })));
const BestLoanFinderDubai = lazy(() => import('./pages/seo').then(m => ({ default: m.BestLoanFinderDubai })));
const SmartLoanCalculator = lazy(() => import('./pages/seo').then(m => ({ default: m.SmartLoanCalculator })));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-3 border-[#0071E3] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-sm text-[#6e6e73] font-inter">Loading...</p>
    </div>
  </div>
);

// Analytics wrapper component to track page views on route changes
const AnalyticsTracker = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Initialize analytics on first load
    initAnalytics();
  }, []);

  useEffect(() => {
    // Track page view on route change
    const pageNames = {
      '/': 'Landing Page',
      '/uae-loan-calculator': 'Calculator',
      '/about-us': 'About',
      '/blog': 'Blog',
      '/admin': 'Admin',
      '/mortgage-uae': 'SEO - Mortgage UAE',
      '/home-loan-dubai': 'SEO - Home Loan Dubai',
      '/personal-loan-finder-uae': 'SEO - Personal Loan',
      '/motor-loan-uae': 'SEO - Motor Loan',
      '/mortgage-calculator-uae': 'SEO - Mortgage Calculator',
      '/refinance-mortgage-uae': 'SEO - Refinance',
      '/best-loan-finder-dubai': 'SEO - Loan Finder',
      '/smart-loan-calculator': 'SEO - Smart Calculator'
    };

    let pageName = pageNames[location.pathname];
    
    // Handle dynamic blog routes
    if (!pageName && location.pathname.startsWith('/blog/')) {
      pageName = 'Blog Article';
    }
    
    if (pageName) {
      trackPageView(pageName, location.pathname);
    }
  }, [location]);

  return children;
};

function App() {
  return (
    <ContentProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
            <AnalyticsTracker>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Main Pages */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/uae-loan-calculator" element={<UAELoanCalculator />} />
                  <Route path="/about-us" element={<AboutPage />} />
                  
                  {/* SEO Landing Pages */}
                  <Route path="/mortgage-uae" element={<MortgageUAE />} />
                  <Route path="/home-loan-dubai" element={<HomeLoanDubai />} />
                  <Route path="/personal-loan-finder-uae" element={<PersonalLoanFinderUAE />} />
                  <Route path="/motor-loan-uae" element={<MotorLoanUAE />} />
                  <Route path="/mortgage-calculator-uae" element={<MortgageCalculatorUAE />} />
                  <Route path="/refinance-mortgage-uae" element={<RefinanceMortgageUAE />} />
                  <Route path="/best-loan-finder-dubai" element={<BestLoanFinderDubai />} />
                  <Route path="/smart-loan-calculator" element={<SmartLoanCalculator />} />
                  
                  {/* Blog Pages */}
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogArticle />} />
                  
                  {/* Admin Dashboard */}
                  <Route path="/admin" element={<AdminDashboard />} />
                </Routes>
              </Suspense>
            </AnalyticsTracker>
            <Toaster position="top-right" />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </ContentProvider>
  );
}

export default App;
